import React, { useRef, useCallback, useState, useEffect } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import { RotateCcw, Check } from 'lucide-react';

interface SignaturePadProps {
  /** Called when the user confirms the signature. Receives base64 PNG data URL. */
  onSignatureCapture: (dataUrl: string) => void;
  /** Optional label above the pad */
  label?: string;
  /** Width in pixels (default: 500) */
  width?: number;
  /** Height in pixels (default: 150) */
  height?: number;
  /** Whether the pad is disabled */
  disabled?: boolean;
  /** Optional existing signature to display */
  existingSignature?: string;
  /** Pen color */
  penColor?: string;
  /** Optional class name for outer container */
  className?: string;
}

/**
 * Reusable visual signature capture pad using react-signature-canvas.
 *
 * Usage:
 *   <SignaturePad onSignatureCapture={(base64) => setSignatureImage(base64)} />
 */
export function SignaturePad({
  onSignatureCapture,
  label = 'Draw Your Signature',
  width = 500,
  height = 150,
  disabled = false,
  existingSignature,
  penColor = '#000000',
  className = '',
}: SignaturePadProps) {
  const sigRef = useRef<SignatureCanvas | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [hasDrawn, setHasDrawn] = useState(false);
  const [confirmed, setConfirmed] = useState(!!existingSignature);
  const [canvasWidth, setCanvasWidth] = useState(width);

  // Responsive: fit container width
  useEffect(() => {
    const resize = () => {
      if (containerRef.current) {
        const w = containerRef.current.clientWidth - 2; // minus border
        setCanvasWidth(Math.min(w, width));
      }
    };
    resize();
    window.addEventListener('resize', resize);
    return () => window.removeEventListener('resize', resize);
  }, [width]);

  const handleClear = useCallback(() => {
    sigRef.current?.clear();
    setHasDrawn(false);
    setConfirmed(false);
    onSignatureCapture('');
  }, [onSignatureCapture]);

  const handleConfirm = useCallback(() => {
    if (!sigRef.current || sigRef.current.isEmpty()) return;
    // Use getCanvas() instead of getTrimmedCanvas() — the latter relies on
    // trim-canvas which breaks in production minified builds (wre is not a function).
    const canvas = sigRef.current.getCanvas();
    const dataUrl = canvas.toDataURL('image/png');
    setConfirmed(true);
    onSignatureCapture(dataUrl);
  }, [onSignatureCapture]);

  const handleEnd = useCallback(() => {
    if (sigRef.current && !sigRef.current.isEmpty()) {
      setHasDrawn(true);
    }
  }, []);

  if (confirmed && existingSignature) {
    // Show confirmed signature
    return (
      <div className={`space-y-2 ${className}`}>
        <p className="text-sm text-gray-600 font-medium">{label}</p>
        <div className="border-2 border-green-200 rounded-lg bg-green-50/30 p-3">
          <img
            src={existingSignature}
            alt="Your signature"
            className="max-h-24 mx-auto"
          />
          <div className="flex items-center justify-center gap-1 mt-2 text-xs text-green-600">
            <Check className="h-3 w-3" />
            Signature captured
          </div>
          {!disabled && (
            <button
              type="button"
              onClick={handleClear}
              className="mt-2 text-xs text-gray-500 hover:text-gray-700 underline mx-auto block"
            >
              Re-sign
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={`space-y-2 ${className}`} ref={containerRef}>
      <p className="text-sm text-gray-600 font-medium">{label}</p>
      <div
        className={`border-2 rounded-lg relative ${
          confirmed
            ? 'border-green-200 bg-green-50/30'
            : disabled
            ? 'border-gray-200 bg-gray-50 opacity-60'
            : 'border-gray-300 bg-white hover:border-gray-400'
        }`}
      >
        <SignatureCanvas
          ref={sigRef}
          penColor={penColor}
          canvasProps={{
            width: canvasWidth,
            height,
            className: 'w-full rounded-lg cursor-crosshair',
            style: { touchAction: 'none' },
          }}
          onEnd={handleEnd}
        />

        {/* Signature line */}
        <div className="absolute bottom-8 left-6 right-6 border-b border-gray-300" />
        <p className="absolute bottom-2 left-6 text-xs text-gray-400">Sign above the line</p>
      </div>

      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={handleClear}
          disabled={disabled || !hasDrawn}
          className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <RotateCcw className="h-3 w-3" />
          Clear
        </button>

        {hasDrawn && !confirmed && (
          <button
            type="button"
            onClick={handleConfirm}
            className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-white bg-green-600 hover:bg-green-700 rounded-md transition-colors"
          >
            <Check className="h-3 w-3" />
            Confirm Signature
          </button>
        )}

        {confirmed && (
          <span className="flex items-center gap-1 text-xs text-green-600">
            <Check className="h-3 w-3" />
            Signature captured
          </span>
        )}
      </div>
    </div>
  );
}

export default SignaturePad;
